import { getAuthToken } from "./AuthToken";
// Get base URL from environment or use default
const ENV_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";
// Ensure we always have /api/v1 prefix
const BASE_URL = ENV_BASE_URL.endsWith("/api/v1")
  ? ENV_BASE_URL
  : `${ENV_BASE_URL.replace(/\/$/, "")}/api/v1`;
type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export const fetchApi = async <T>(
  endpoint: string,
  method: HttpMethod,
  input?: unknown
): Promise<T> => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");

  // ✅ await the token
  const accessToken = await getAuthToken();
  if (accessToken) {
    headers.append("Authorization", `Bearer ${accessToken}`);
  }

  // request config
  const requestOptions: RequestInit = {
    method,
    headers,
    redirect: "follow",
    ...(input && method !== "GET" ? { body: JSON.stringify(input) } : {}),
  };

  // fetch call - ensure no double slashes
  const url = `${BASE_URL}${endpoint}`.replace(/([^:]\/)\/+/g, "$1");
  const response = await fetch(url, requestOptions);

  // optional: handle errors
  if (!response.ok) {
    // Try to get error message from response body
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      }
    } catch {
      // If response is not JSON, use status text
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  // Handle 204 No Content responses (common for DELETE operations)
  if (response.status === 204) {
    return undefined as T;
  }

  // Check if response has content
  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    // If no JSON content, return empty or text response
    const text = await response.text();
    return (text || undefined) as T;
  }

  // parse response as JSON
  const responseData = await response.json();

  // Handle wrapped response format from backend
  if (
    responseData &&
    typeof responseData === "object" &&
    "data" in responseData
  ) {
    return responseData.data as T;
  }

  return responseData as T;
};
